import { useIntl } from 'react-intl';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { GA4ItemListName } from '@jsmdg/tracking';
import { Breakpoint, ButtonColor, NewProductTile, useBreakpoint } from '@jsmdg/yoshi';
import { ProductAttribute } from '../../../shared/enums/productAttribute';
import { ProductType } from '../../../shared/enums/productType';
import { type FragmentContext } from '../../../shared/types/fragmentContext';
import { type Product } from '../../../shared/types/searchResponse';
import { getProductTileDuration } from '../../helper/getProductTileDuration';
import { getProductTileMessages } from '../../helper/getProductTileMessages';
import { renderPrice } from '../../helper/renderPrice';
import { useOnProductTileClickAndImpression } from '../../hooks/useOnProductTileClickAndImpression';
import { useProductTileData } from '../../hooks/useProductTileData';
import { useWishListToggle } from '../../hooks/useWishListToggle';
import { messagesProductTile } from './productTileMessages';

const messages = getProductTileMessages();

type ProductTileWrapperProps = {
    readonly product: Product;
    readonly position: number;
    readonly lazyLoad: boolean;
    readonly trackingListName?: GA4ItemListName;
    readonly queryId?: string;
    readonly indexName?: string;
    readonly hasLocationFilter?: boolean;
};

const ProductTileWrapper = ({
    hasLocationFilter,
    indexName,
    lazyLoad,
    position,
    product,
    queryId,
    trackingListName = GA4ItemListName.Category,
}: ProductTileWrapperProps): JSX.Element => {
    const intl = useIntl();

    const isDesktop = useBreakpoint(Breakpoint.XS);

    const { customerState, errorLogger, initialWishlist, isMydays, tenantConfig } =
        useFragmentContext<FragmentContext>();

    const {
        discountBadge,
        getLocationLabel,
        getParticipantsLabel,
        getPricePrefix,
        hasClosestLocation,
        images,
        locale,
        onCloseServices,
        onImageSliderSlide,
        onLearnMoreClick,
        onLinkTextClick,
        originalPrice,
        price,
        url,
    } = useProductTileData(
        product,
        intl,
        {
            trackingListName,
            hasLocationFilter,
            indexName,
            queryId,
        },
        messages,
    );

    const { onProductTileClick, sendObjectClick, setImpressionTrackedElement } =
        useOnProductTileClickAndImpression(product, position, intl, {
            indexName,
            queryId,
            trackingListName,
            hasLocationFilter,
        });

    const wishlistMessages = {
        a11yCloseErrorMessage: intl.formatMessage(messages.a11yCloseErrorMessage),
        tooManyWishlistArticlesError: intl.formatMessage(messages.tooManyWishlistArticlesError),
    };

    const { isOnWishlist, onWishlistButtonClick } = useWishListToggle(
        product,
        wishlistMessages,
        {
            customerState,
            errorLogger,
            initialWishlist,
        },
        trackingListName,
        hasLocationFilter,
    );

    const a11yWishlistButton = isOnWishlist
        ? intl.formatMessage(messages.screenReaderTextForWishlistRemove)
        : intl.formatMessage(messages.screenReaderTextForWishlistAdd);

    const {
        activityDuration,
        closestLocation,
        inPreview,
        isTestProduct,
        locations,
        participants,
        productType,
        rating,
        services,
        title,
        travelNights,
    } = product;

    const isValueVoucherProduct = productType === ProductType.ValueVoucherProduct;
    const productInfos = isValueVoucherProduct
        ? [
              intl.formatMessage(messages.valueVoucherProductInfoFlexible, {
                  currencySymbol: tenantConfig.currency.symbol,
              }),
              intl.formatMessage(messages.valueVoucherProductInfoDelivery),
              intl.formatMessage(messages.valueVoucherProductInfoGiftWrap),
          ]
        : undefined;

    const learnMoreText = intl.formatMessage(messages.learnMore);
    const servicesLinkText = intl.formatMessage(
        isMydays ? messages.servicesLinkTextMD : messages.servicesLinkTextJS,
    );

    return (
        <NewProductTile
            name={title}
            price={renderPrice(price, intl)}
            originalPrice={renderPrice(originalPrice, intl)}
            pricePrefix={getPricePrefix()}
            images={isDesktop ? images : images.slice(0, 1)}
            url={url}
            locale={locale}
            closestLocation={hasClosestLocation ? closestLocation : undefined}
            locations={locations}
            attributes={[
                inPreview ? ProductAttribute.Preview : '',
                isTestProduct ? ProductAttribute.Test : '',
            ].filter(Boolean)}
            services={
                isDesktop && services
                    ? {
                          linkText: servicesLinkText,
                          overlayTitle: intl.formatMessage(
                              isMydays ? messages.overlayTitleMD : messages.overlayTitleJS,
                          ),
                          caption: intl.formatMessage(messages.servicesCaptionMessage),
                          body: services.body,
                          a11yCloseServicesLabel: intl.formatMessage(
                              messages.a11YCloseServicesMessage,
                          ),
                          onLinkTextClick: async (willShowServices: boolean) =>
                              onLinkTextClick(willShowServices, servicesLinkText),
                          onCloseServices,
                      }
                    : undefined
            }
            showServices={isDesktop && !isValueVoucherProduct}
            productInfos={productInfos}
            addToCartButtonData={{
                label: learnMoreText,
                onClick: async event => {
                    event?.preventDefault();
                    await sendObjectClick();
                    await onLearnMoreClick(learnMoreText, event);
                },
                color: ButtonColor.Brand,
            }}
            onImageSliderSlide={onImageSliderSlide}
            rating={rating}
            lazyLoad={lazyLoad}
            maxParticipants={participants?.max}
            ref={setImpressionTrackedElement}
            locationLabel={getLocationLabel()}
            participantsLabel={getParticipantsLabel()}
            {...getProductTileDuration({ travelNights, activityDuration, intl, messages })}
            onProductClick={onProductTileClick}
            discountBadge={discountBadge}
            showWishlistButton
            toggleWishlist={onWishlistButtonClick}
            a11yWishlistButton={a11yWishlistButton}
            isOnWishlist={isOnWishlist}
            a11yExperienceText={title}
            a11yTextRating={`${intl.formatMessage(messagesProductTile.ratingText, {
                starRating: rating?.value || 0,
                amountOfRatings: rating?.count || 0,
            })}`}
            a11yOriginalPrice={intl.formatMessage(messagesProductTile.originalPriceText)}
            a11yCurrentPrice={intl.formatMessage(messagesProductTile.currentPriceText)}
            a11yLocationText={intl.formatMessage(messagesProductTile.locationText)}
            a11yAmountOfParticipants={intl.formatMessage(
                messagesProductTile.amountOfParticipantsText,
            )}
            a11yButtonDetail={intl.formatMessage(messagesProductTile.buttonDetailText)}
            a11yNextSlide={intl.formatMessage(messagesProductTile.a11yNextSlide)}
            a11yPreviousSlide={intl.formatMessage(messagesProductTile.a11yPreviousSlide)}
            a11yCloseLocationText={intl.formatMessage(messagesProductTile.closeLocationText)}
        />
    );
};

export { ProductTileWrapper };
